import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  QrCode, 
  Mail, 
  KeyRound, 
  Smartphone, 
  RefreshCw, 
  Trash, 
  CheckCircle, 
  AlertTriangle, 
  Info,
  Star,
  Pencil,
  Save
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

/**
 * MFATester Component for testing Multi-Factor Authentication features
 * This component allows developers to test:
 * - TOTP setup and verification
 * - Email MFA setup and verification
 * - Trusted device management
 * - MFA settings management
 */
const MFATester = () => {
  const [loading, setLoading] = useState({});
  const [mfaStatus, setMfaStatus] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [emailSent, setEmailSent] = useState(false);
  const [deviceName, setDeviceName] = useState('My Device');
  const [mfaMethods, setMfaMethods] = useState([]);
  const [editingMethodId, setEditingMethodId] = useState(null);
  const [editedName, setEditedName] = useState('');
  
  // On component mount, fetch all relevant MFA data
  useEffect(() => {
    fetchMfaStatus();
    fetchTrustedDevices();
    fetchMfaMethods();
    
    // Log initial state for debugging
    console.log('Initial mfaMethods state:', mfaMethods);
  }, []);
  
  // Debug when mfaMethods changes
  useEffect(() => {
    console.log('mfaMethods updated:', mfaMethods);
  }, [mfaMethods]);

  // Fetch the current MFA status from the server
  const fetchMfaStatus = async () => {
    setLoading(prev => ({ ...prev, status: true }));
    try {
      const response = await api.get('/api/mfa/status/');
      setMfaStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch MFA status:', error);
      toast.error('Failed to fetch MFA status');
    } finally {
      setLoading(prev => ({ ...prev, status: false }));
    }
  };

  // Fetch trusted devices
  const fetchTrustedDevices = async () => {
    setLoading(prev => ({ ...prev, devices: true }));
    try {
      const response = await api.get('/api/mfa/trusted-devices/');
      console.log('Trusted devices API response:', response.data);
      
      // Handle paginated response
      if (response.data && response.data.results) {
        setTrustedDevices(response.data.results);
      } else {
        setTrustedDevices(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch trusted devices:', error);
    } finally {
      setLoading(prev => ({ ...prev, devices: false }));
    }
  };
  
  // Fetch MFA methods (devices)
  const fetchMfaMethods = async () => {
    setLoading(prev => ({ ...prev, methods: true }));
    try {
      const response = await api.get('/api/mfa/methods/');
      console.log('MFA methods API response:', response.data);
      
      // Django REST Framework typically returns paginated responses
      // with results in a 'results' array
      if (response.data && response.data.results) {
        console.log('Setting MFA methods from results array:', response.data.results);
        setMfaMethods(response.data.results);
      } else {
        console.log('Setting MFA methods directly:', response.data);
        setMfaMethods(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch MFA methods:', error);
      toast.error('Failed to fetch MFA methods');
    } finally {
      setLoading(prev => ({ ...prev, methods: false }));
    }
  };
  
  // Delete an MFA method
  const deleteMfaMethod = async (methodId) => {
    setLoading(prev => ({ ...prev, [`method_${methodId}`]: true }));
    try {
      await api.delete(`/api/mfa/methods/${methodId}/`);
      toast.success('MFA device deleted successfully');
      fetchMfaMethods(); // Refresh the list after deletion
      fetchMfaStatus(); // Also refresh status as it may have changed
    } catch (error) {
      console.error('Failed to delete MFA method:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to delete MFA method: ${errorMsg}`);
      } else {
        toast.error('Failed to delete MFA method');
      }
    } finally {
      setLoading(prev => ({ ...prev, [`method_${methodId}`]: false }));
    }
  };
  
  // Set an MFA method as primary
  const setPrimaryMethod = async (methodId) => {
    setLoading(prev => ({ ...prev, [`primary_${methodId}`]: true }));
    try {
      await api.post(`/api/mfa/methods/${methodId}/set_primary/`);
      toast.success('Primary MFA device updated successfully');
      fetchMfaMethods(); // Refresh the list
    } catch (error) {
      console.error('Failed to set primary MFA method:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to set primary MFA method: ${errorMsg}`);
      } else {
        toast.error('Failed to set primary MFA method');
      }
    } finally {
      setLoading(prev => ({ ...prev, [`primary_${methodId}`]: false }));
    }
  };
  
  // Start editing an MFA method name
  const startEditingMethod = (method) => {
    setEditingMethodId(method.id);
    setEditedName(method.device_name);
  };
  
  // Cancel editing
  const cancelEditingMethod = () => {
    setEditingMethodId(null);
    setEditedName('');
  };
  
  // Save edited MFA method name
  const saveMethodName = async (methodId) => {
    if (!editedName.trim()) {
      toast.error('Device name cannot be empty');
      return;
    }
    
    setLoading(prev => ({ ...prev, [`save_${methodId}`]: true }));
    try {
      await api.patch(`/api/mfa/methods/${methodId}/`, { device_name: editedName.trim() });
      toast.success('Device name updated successfully');
      fetchMfaMethods(); // Refresh the list
      cancelEditingMethod();
    } catch (error) {
      console.error('Failed to update MFA method name:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to update device name: ${errorMsg}`);
      } else {
        toast.error('Failed to update device name');
      }
    } finally {
      setLoading(prev => ({ ...prev, [`save_${methodId}`]: false }));
    }
  };

  // Setup TOTP MFA
  const setupTOTP = async () => {
    if (!deviceName.trim()) {
      toast.error('Please enter a device name for your device');
      return;
    }
    
    setLoading(prev => ({ ...prev, totp: true }));
    try {
      const response = await api.post('/api/mfa/totp/setup/', { device_name: deviceName });
      setQrCodeData(response.data);
      toast.success('TOTP setup initiated - scan the QR code with your authenticator app');
    } catch (error) {
      console.error('Failed to setup TOTP:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to setup TOTP: ${errorMsg}`);
      } else {
        toast.error('Failed to setup TOTP');
      }
    } finally {
      setLoading(prev => ({ ...prev, totp: false }));
    }
  };

  // Verify TOTP code
  const verifyTOTP = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit verification code');
      return;
    }

    setLoading(prev => ({ ...prev, verify: true }));
    try {
      await api.post('/api/mfa/totp/verify/', { 
        code: verificationCode,
        device_name: deviceName
      });
      toast.success('TOTP verification successful!');
      setQrCodeData(null); // Clear QR code after successful verification
      fetchMfaStatus(); // Refresh MFA status
      setVerificationCode(''); // Clear the verification code input
    } catch (error) {
      console.error('Failed to verify TOTP:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to verify TOTP: ${errorMsg}`);
      } else {
        toast.error('Failed to verify TOTP code');
      }
    } finally {
      setLoading(prev => ({ ...prev, verify: false }));
    }
  };

  // Setup Email MFA
  const setupEmailMFA = async () => {
    if (!deviceName.trim()) {
      toast.error('Please enter a device name');
      return;
    }
    
    setLoading(prev => ({ ...prev, email: true }));
    try {
      await api.post('/api/mfa/email/setup/', { device_name: deviceName });
      setEmailSent(true);
      toast.success('Verification email sent');
    } catch (error) {
      console.error('Failed to setup Email MFA:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to setup Email MFA: ${errorMsg}`);
      } else {
        toast.error('Failed to setup Email MFA');
      }
    } finally {
      setLoading(prev => ({ ...prev, email: false }));
    }
  };

  // Verify Email MFA
  const verifyEmailMFA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit verification code');
      return;
    }

    setLoading(prev => ({ ...prev, verifyEmail: true }));
    try {
      await api.post('/api/mfa/email/verify/', { 
        code: verificationCode,
        device_name: deviceName 
      });
      toast.success('Email verification successful!');
      setEmailSent(false); // Reset email sent status
      fetchMfaStatus(); // Refresh MFA status
      setVerificationCode(''); // Clear the verification code input
    } catch (error) {
      console.error('Failed to verify email code:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to verify email: ${errorMsg}`);
      } else {
        toast.error('Failed to verify email code');
      }
    } finally {
      setLoading(prev => ({ ...prev, verifyEmail: false }));
    }
  };

  // Toggle MFA settings
  const toggleMfaSettings = async (enabled) => {
    setLoading(prev => ({ ...prev, settings: true }));
    try {
      await api.put('/api/mfa/settings/', {
        is_mfa_enabled: enabled
      });
      toast.success(`MFA ${enabled ? 'enabled' : 'disabled'} successfully`);
      fetchMfaStatus(); // Refresh MFA status
    } catch (error) {
      console.error('Failed to update MFA settings:', error);
      toast.error('Failed to update MFA settings');
    } finally {
      setLoading(prev => ({ ...prev, settings: false }));
    }
  };

  // Remove a trusted device
  const removeTrustedDevice = async (deviceId) => {
    setLoading(prev => ({ ...prev, [`device_${deviceId}`]: true }));
    try {
      await api.delete(`/api/mfa/trusted-devices/${deviceId}/`);
      toast.success('Trusted device removed');
      fetchTrustedDevices(); // Refresh trusted devices
    } catch (error) {
      console.error('Failed to remove trusted device:', error);
      toast.error('Failed to remove trusted device');
    } finally {
      setLoading(prev => ({ ...prev, [`device_${deviceId}`]: false }));
    }
  };

  // Test MFA challenge
  const testMfaChallenge = async () => {
    setLoading(prev => ({ ...prev, challenge: true }));
    try {
      // Get the current user's username
      const userResponse = await api.get('/api/user/');
      const username = userResponse.data.username || 'test_user';
      
      const response = await api.post('/api/mfa/login/challenge/', {
        username: username // Use the actual logged-in username
      });
      toast.success('MFA challenge initiated');
      console.log('MFA Challenge response:', response.data);
    } catch (error) {
      console.error('Failed to initiate MFA challenge:', error);
      if (error.response?.data) {
        const errorMsg = Object.values(error.response.data).flat().join(', ');
        toast.error(`Failed to initiate MFA challenge: ${errorMsg}`);
      } else {
        toast.error('Failed to initiate MFA challenge');
      }
    } finally {
      setLoading(prev => ({ ...prev, challenge: false }));
    }
  };

  return (
    <div className="space-y-8">
      {/* MFA Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6"
      >
        <div className="flex items-center mb-4">
          <Shield className="h-6 w-6 text-blue-600 mr-3" />
          <h3 className="text-xl font-bold text-gray-800">MFA Status</h3>
          <button 
            onClick={fetchMfaStatus}
            className="ml-auto text-gray-400 hover:text-gray-600"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>

        {loading.status ? (
          <div className="flex justify-center p-4">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : mfaStatus ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">MFA Enabled</div>
              <div className="text-lg font-semibold mt-1">
                {mfaStatus.is_mfa_enabled ? (
                  <span className="text-green-600 flex items-center">
                    <CheckCircle className="h-5 w-5 mr-1" /> Enabled
                  </span>
                ) : (
                  <span className="text-red-500">Disabled</span>
                )}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">Available Methods</div>
              <div className="text-lg font-semibold mt-1">
                {mfaStatus.available_methods?.length 
                  ? mfaStatus.available_methods.join(', ') 
                  : 'None'}
              </div>
            </div>
            {mfaStatus.is_mfa_required !== undefined && (
              <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                <div className="text-sm font-medium text-gray-500">MFA Required</div>
                <div className="text-lg font-semibold mt-1">
                  {mfaStatus.is_mfa_required ? 'Yes' : 'No'}
                </div>
              </div>
            )}
            {mfaStatus.remember_device_days !== undefined && (
              <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                <div className="text-sm font-medium text-gray-500">Remember Device For</div>
                <div className="text-lg font-semibold mt-1">
                  {mfaStatus.remember_device_days} days
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-4 text-gray-500">
            No MFA status information available
          </div>
        )}

        <div className="flex mt-6 space-x-4 justify-end">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => toggleMfaSettings(true)}
            disabled={loading.settings || (mfaStatus?.is_mfa_enabled)}
            className={`px-4 py-2 rounded-lg font-medium text-sm ${
              loading.settings || (mfaStatus?.is_mfa_enabled)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Enable MFA
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => toggleMfaSettings(false)}
            disabled={loading.settings || !(mfaStatus?.is_mfa_enabled)}
            className={`px-4 py-2 rounded-lg font-medium text-sm ${
              loading.settings || !(mfaStatus?.is_mfa_enabled)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            Disable MFA
          </motion.button>
        </div>
      </motion.div>

      {/* TOTP Setup */}
      {/* Device Name Input - common for all MFA methods */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6"
      >
        <div className="flex items-center mb-4">
          <Smartphone className="h-6 w-6 text-gray-600 mr-3" />
          <h3 className="text-xl font-bold text-gray-800">Device Settings</h3>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex">
            <Info className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">How to use this tester:</p>
              <ol className="list-decimal ml-5 mt-1 space-y-1">
                <li>Enter a device name below (e.g., "iPhone 13", "Work Laptop")</li>
                <li>Choose an MFA method to set up (TOTP or Email)</li>
                <li>Follow the setup instructions for your chosen method</li>
                <li>Verify using the code from your authenticator app or email</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="device-name" className="block text-sm font-medium text-gray-700 mb-1">
            Device Name
          </label>
          <div className="flex">
            <input
              id="device-name"
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="Enter a recognizable name for this device"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            This should identify the physical device you're using for MFA (e.g., "Jake's iPhone")
          </p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, transition: { delay: 0.1 } }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6"
      >
        <div className="flex items-center mb-4">
          <QrCode className="h-6 w-6 text-purple-600 mr-3" />
          <h3 className="text-xl font-bold text-gray-800">TOTP Authentication</h3>
        </div>

        <p className="text-gray-600 mb-4">
          Test Time-Based One-Time Password (TOTP) authentication setup and verification.
        </p>

        {qrCodeData && (
          <div className="mb-6">
            <div className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <div className="mb-4 p-4 bg-white dark:bg-gray-800 rounded-lg">
                {/* Display QR code image */}
                <img 
                  src={qrCodeData.qr_code} 
                  alt="TOTP QR Code" 
                  className="w-48 h-48 mx-auto"
                />
              </div>
              <div className="text-sm text-gray-600 mb-2">
                Scan this QR code with your authenticator app
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 mb-1">Or use the secret key:</span>
                <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-sm font-mono select-all">
                  {qrCodeData.secret}
                </code>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-end sm:space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={setupTOTP}
              disabled={loading.totp}
              className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center justify-center ${
                loading.totp
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {loading.totp ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <>
                  <QrCode className="h-5 w-5 mr-2" />
                  Generate QR Code
                </>
              )}
            </motion.button>
          </div>

          <div className="mt-4">
            <label htmlFor="verification-code" className="block text-sm font-medium text-gray-700 mb-1">
              Verification Code
            </label>
            <div className="flex">
              <input
                id="verification-code"
                type="text"
                maxLength="6"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-l-lg focus:ring-blue-500 focus:border-blue-500"
              />
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={verifyTOTP}
                disabled={loading.verify || !verificationCode}
                className={`px-4 py-2 rounded-r-lg font-medium text-sm flex items-center ${
                  loading.verify || !verificationCode
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {loading.verify ? (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  'Verify'
                )}
              </motion.button>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>
        </div>
      </motion.div>

      {/* Email MFA Setup */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, transition: { delay: 0.2 } }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6"
      >
        <div className="flex items-center mb-4">
          <Mail className="h-6 w-6 text-blue-600 mr-3" />
          <h3 className="text-xl font-bold text-gray-800">Email Authentication</h3>
        </div>

        <div>
          {!emailSent ? (
            <div>
              <div className="mb-4">
                <p className="text-gray-700 mb-2">
                  Email MFA sends a verification code to your email address for an extra layer of security.
                </p>
                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h4 className="font-medium text-gray-700 mb-2">How it works:</h4>
                  <ol className="list-decimal ml-5 text-sm text-gray-600 space-y-1">
                    <li>Click "Send Verification Email" (make sure you've entered a device name first)</li>
                    <li>Check your email for the verification code</li>
                    <li>Enter the 6-digit code from the email to complete setup</li>
                  </ol>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={setupEmailMFA}
                disabled={loading.email}
                className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center justify-center ${loading.email ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
              >
                {loading.email ? (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <>
                    <Mail className="h-5 w-5 mr-2" />
                    Send Verification Email
                  </>
                )}
              </motion.button>
            </div>
          ) : (
            <div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                  <p className="text-sm text-green-700">Verification email sent! Check your inbox for a 6-digit code.</p>
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="email-code" className="block text-sm font-medium text-gray-700 mb-1">
                  Enter Code From Your Email
                </label>
                <div className="flex">
                  <input
                    id="email-code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').substring(0, 6))}
                    placeholder="Enter 6-digit code"
                    maxLength="6"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={verifyEmailMFA}
                    disabled={loading.verifyEmail || verificationCode.length !== 6}
                    className={`ml-4 px-4 py-2 rounded-lg font-medium text-sm flex items-center justify-center ${loading.verifyEmail || verificationCode.length !== 6 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}
                  >
                    {loading.verifyEmail ? (
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Verify
                      </>
                    )}
                  </motion.button>
                </div>
                <div className="mt-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setEmailSent(false);
                      setVerificationCode('');
                    }}
                    className="px-4 py-2 rounded-lg font-medium text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </motion.button>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Trusted Devices */}
      {/* MFA Methods/Devices Management */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, transition: { delay: 0.2 } }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6"
      >
        <div className="flex items-center mb-4">
          <Smartphone className="h-6 w-6 text-green-600 mr-3" />
          <h3 className="text-xl font-bold text-gray-800">MFA Devices</h3>
          <button 
            onClick={fetchMfaMethods}
            className="ml-auto text-gray-400 hover:text-gray-600"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>

        <p className="text-gray-600 mb-4">
          Manage your MFA devices and methods. You can rename, delete, or set a device as primary.
        </p>

        {/* Debugging info */}
        <div className="text-sm text-gray-500 mb-4">
          Debug - Methods state: {mfaMethods ? `${mfaMethods.length} methods` : 'null'}
          {mfaMethods && mfaMethods.length > 0 && (
            <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-700 rounded-md overflow-auto max-h-40 text-xs">
              {JSON.stringify(mfaMethods, null, 2)}
            </pre>
          )}
        </div>
        
        {loading.methods ? (
          <div className="flex justify-center p-4">
            <svg className="animate-spin h-8 w-8 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : mfaMethods && mfaMethods.length > 0 ? (
          <div className="space-y-4">
            {mfaMethods.map((method) => (
              <div key={method.id} className={`border rounded-lg overflow-hidden ${method.is_primary ? 'border-green-400 bg-green-50' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    {/* Device info */}
                    <div className="flex items-center">
                      {method.method_type === 'totp' ? (
                        <QrCode className={`h-5 w-5 ${method.is_primary ? 'text-green-600' : 'text-gray-600'} mr-3`} />
                      ) : (
                        <Mail className={`h-5 w-5 ${method.is_primary ? 'text-green-600' : 'text-gray-600'} mr-3`} />
                      )}
                      
                      {/* If currently editing this method */}
                      {editingMethodId === method.id ? (
                        <div className="flex items-center">
                          <input
                            type="text"
                            value={editedName}
                            onChange={(e) => setEditedName(e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded mr-2"
                            autoFocus
                          />
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => saveMethodName(method.id)}
                              disabled={loading[`save_${method.id}`]}
                              className="text-green-600 hover:text-green-800"
                            >
                              {loading[`save_${method.id}`] ? (
                                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <Save className="h-5 w-5" />
                              )}
                            </button>
                            <button 
                              onClick={cancelEditingMethod}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center">
                            <span className="font-medium text-gray-900">
                              {method.device_name}
                            </span>
                            {method.is_primary && (
                              <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                Primary
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            Type: {method.method_type === 'totp' ? 'Authenticator App' : 'Email'}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Action buttons */}
                    {editingMethodId !== method.id && (
                      <div className="flex space-x-3">
                        {/* Edit button */}
                        <button
                          onClick={() => startEditingMethod(method)}
                          className="text-blue-500 hover:text-blue-700"
                          title="Rename device"
                        >
                          <Pencil className="h-5 w-5" />
                        </button>
                        
                        {/* Set as primary button (disabled if already primary) */}
                        {!method.is_primary && (
                          <button
                            onClick={() => setPrimaryMethod(method.id)}
                            disabled={loading[`primary_${method.id}`]}
                            className="text-amber-500 hover:text-amber-700"
                            title="Set as primary device"
                          >
                            {loading[`primary_${method.id}`] ? (
                              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <Star className="h-5 w-5" />
                            )}
                          </button>
                        )}
                        
                        {/* Delete button */}
                        <button
                          onClick={() => deleteMfaMethod(method.id)}
                          disabled={loading[`method_${method.id}`]}
                          className="text-red-500 hover:text-red-700"
                          title="Delete device"
                        >
                          {loading[`method_${method.id}`] ? (
                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <Trash className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No MFA devices found. Set up a new device using the options below.
          </div>
        )}
      </motion.div>
      
      {/* Trusted Devices */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, transition: { delay: 0.3 } }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6"
      >
        <div className="flex items-center mb-4">
          <Smartphone className="h-6 w-6 text-indigo-600 mr-3" />
          <h3 className="text-xl font-bold text-gray-800">Trusted Devices</h3>
          <button 
            onClick={fetchTrustedDevices}
            className="ml-auto text-gray-400 hover:text-gray-600"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>

        <p className="text-gray-600 mb-4">
          Manage devices that are trusted for MFA.
        </p>

        {loading.devices ? (
          <div className="flex justify-center p-4">
            <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : trustedDevices && trustedDevices.length > 0 ? (
          <div className="space-y-3">
            {trustedDevices.map((device) => (
              <div key={device.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
                <div className="flex items-center">
                  <Smartphone className="h-5 w-5 text-indigo-500 mr-3" />
                  <div>
                    <div className="font-medium text-gray-900">{device.device_name || 'Unknown Device'}</div>
                    <div className="text-xs text-gray-500">
                      Trusted until: {new Date(device.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeTrustedDevice(device.id)}
                  disabled={loading[`device_${device.id}`]}
                  className="text-red-500 hover:text-red-700 ml-4 focus:outline-none"
                >
                  {loading[`device_${device.id}`] ? (
                    <svg className="animate-spin h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <Trash className="h-5 w-5" />
                  )}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No trusted devices found
          </div>
        )}
      </motion.div>

      {/* MFA Challenge Testing */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, transition: { delay: 0.4 } }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6"
      >
        <div className="flex items-center mb-4">
          <KeyRound className="h-6 w-6 text-amber-600 mr-3" />
          <h3 className="text-xl font-bold text-gray-800">Login Flow MFA Challenge Testing</h3>
        </div>

        <div className="mb-4">
          <p className="text-gray-700 mb-2">
            This tests what happens during a login attempt when MFA is enabled for a user.
          </p>
          <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="font-medium text-gray-700 mb-2">What this does:</h4>
            <ol className="list-decimal ml-5 text-sm text-gray-600 space-y-1">
              <li>Simulates a user attempting to log in with correct username/password</li>
              <li>Triggers the MFA challenge step that would occur during login</li>
              <li>Returns information about which MFA methods are available</li>
            </ol>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0" />
            <div className="text-sm text-yellow-700">
              <p className="font-medium">Before testing:</p>
              <ul className="list-disc ml-5 mt-1">
                <li>You must have at least one MFA method set up and enabled</li>
                <li>This will use your current logged-in user for the test</li>
              </ul>
            </div>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={testMfaChallenge}
          disabled={loading.challenge}
          className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center justify-center ${
            loading.challenge
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-amber-600 text-white hover:bg-amber-700'
          }`}
        >
          {loading.challenge ? (
            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <>
              <KeyRound className="h-5 w-5 mr-2" />
              Test MFA Challenge
            </>
          )}
        </motion.button>
      </motion.div>
    </div>
  );
};

export default MFATester;
